import React, { useEffect, useState } from 'react';
import { packagesService } from '../services/packagesService';
import DestinationAutocomplete from '../components/DestinationAutocomplete';
import { useApp } from '../context/AppContext';
import HolidayPackageForm from '../components/HolidayPackageForm';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import DateSlotsSidebar from '../components/DateSlotsSidebar';
import { 
  MagnifyingGlassIcon,
  MapPinIcon,
  TagIcon,
  FunnelIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  TicketIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const HolidayPackagesList = () => {
    const { showToast } = useApp();
    const [packages, setPackages] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        destination: '',
        category: '',
        search: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editPackage, setEditPackage] = useState(null);
  const [selectedPackageForSlots, setSelectedPackageForSlots] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    packageId: null,
    packageTitle: '',
    loading: false
  });
  // Removed destinations state - now using autocomplete component

    // Categories for filter dropdowns
    const categories = ['Adventure', 'Beach', 'Cultural', 'Wildlife', 'Honeymoon',
        'Family', 'Luxury', 'Budget', 'Religious', 'Wellness'];

    useEffect(() => {
        loadHolidayPackages({ page: 1, limit });
    }, [limit]);

    // Removed loadDestinations function - now using autocomplete component

    const loadHolidayPackages = async (filterParams = {}) => {
        try {
            setLoading(true);
            setError(null);
            const response = await packagesService.getHolidayPackages(filterParams);
            // Handle the API response structure: { success: true, data: [...], filters: {...}, pagination: {...} }
            if (response.success && response.data) {
                setPackages(response.data);
                setPagination(response.pagination || null);
            } else {
                setPackages([]);
                setPagination(null);
            }
        } catch (error) {
            console.error('Error loading holiday packages:', error);
            setError('Failed to load holiday packages');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        const params = { page, limit };
        if (filters.search) params.search = filters.search;
        if (filters.destination) params.destination = filters.destination;
        if (filters.category) params.category = filters.category;
        loadHolidayPackages(params);
    };

    const handleApplyFilters = () => {
        setCurrentPage(1); // Reset to first page when applying filters
        const params = { page: 1, limit };
        if (filters.search) params.search = filters.search;
        if (filters.destination) params.destination = filters.destination;
        if (filters.category) params.category = filters.category;
        loadHolidayPackages(params);
    };

    const handleClearFilters = () => {
        setFilters({
            destination: '',
            category: '',
            search: ''
        });
        setCurrentPage(1);
        loadHolidayPackages({ page: 1, limit });
    };

    const handleAddSuccess = (newPackage) => {
        // Add the new package to the list
        setPackages(prev => [newPackage, ...prev]);
        setShowAddForm(false);
    };

    const handleEditClick = (packageData) => {
        setEditPackage(packageData);
    };

    const handleEditSuccess = (updatedPackage, isEdit) => {
        if (isEdit) {
            // Update the package in the list
            setPackages(prev => prev.map(pkg =>
                pkg._id === updatedPackage._id ? updatedPackage : pkg
            ));
        } else {
            // Add new package to the list
            setPackages(prev => [updatedPackage, ...prev]);
        }
        setEditPackage(null);
        
        // Refresh the current page with current filters to maintain pagination
        const params = { page: currentPage, limit };
        if (filters.search) params.search = filters.search;
        if (filters.destination) params.destination = filters.destination;
        if (filters.category) params.category = filters.category;
        loadHolidayPackages(params);
    };

    const handleDeleteClick = (packageId, packageTitle) => {
        setDeleteDialog({
            isOpen: true,
            packageId,
            packageTitle,
            loading: false
        });
    };

    const handleDeleteConfirm = async () => {
        try {
            setDeleteDialog(prev => ({ ...prev, loading: true }));

            const response = await packagesService.deleteHolidayPackage(deleteDialog.packageId);
            
            if (response.success) {
                showToast('success', response.message || 'Package deleted successfully');
                // Remove the package from the list
                setPackages(prev => prev.filter(pkg => pkg._id !== deleteDialog.packageId));
            } else {
                showToast('error', response.message || 'Failed to delete package');
            }

            // Close the dialog
            setDeleteDialog({
                isOpen: false,
                packageId: null,
                packageTitle: '',
                loading: false
            });
        } catch (error) {
            console.error('Error deleting package:', error);
            const errorMessage = error.response?.data?.message || 'Failed to delete package. Please try again.';
            showToast('error', errorMessage);
            
            // Close the dialog on error as well
            setDeleteDialog({
                isOpen: false,
                packageId: null,
                packageTitle: '',
                loading: false
            });
        }
    };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      isOpen: false,
      packageId: null,
      packageTitle: '',
      loading: false
    });
  };

  const handleDateSlotsClick = (packageData) => {
    setSelectedPackageForSlots(packageData);
  };

  const handleSlotAdded = () => {
    // Refresh the current page with current filters to maintain pagination
    const params = { page: currentPage, limit };
    if (filters.search) params.search = filters.search;
    if (filters.destination) params.destination = filters.destination;
    if (filters.category) params.category = filters.category;
    loadHolidayPackages(params);
  };

  const handleStatusChange = async (packageId, newStatus) => {
    // Find the package to check its type and date slots
    const packageData = packages.find(pkg => pkg._id === packageId);
    
    // If trying to publish a Type 2 package without date slots, prevent it
    if (newStatus === 'PUBLISHED' && packageData?.type === 2) {
      const dateSlotsCount = packageData.dateSlots?.length || 0;
      if (dateSlotsCount === 0) {
        showToast('error', 'Cannot publish a schedulable package without date slots. Please choose a date slot first.');
        return;
      }
    }
    
    try {
      const response = await packagesService.updatePackageStatus(packageId, { status: newStatus });
      if (response.success) {
        showToast('success', response.message || 'Package status updated successfully');
        // Refresh the current page with current filters to maintain pagination
        const params = { page: currentPage, limit };
        if (filters.search) params.search = filters.search;
        if (filters.destination) params.destination = filters.destination;
        if (filters.category) params.category = filters.category;
        loadHolidayPackages(params);
      } else {
        const errorMessage = response.message || 'Failed to update package status';
        showToast('error', errorMessage);
      }
    } catch (error) {
      console.error('Error updating package status:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update package status. Please try again.';
      showToast('error', errorMessage);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'text-yellow-600 bg-yellow-100';
      case 'PUBLISHED':
        return 'text-green-600 bg-green-100';
      case 'SUSPENDED':
        return 'text-yellow-600 bg-yellow-100';
      case 'ARCHIVED':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

    // const formatPrice = (price) => {
    //     if (!price) return 'Price not available';
    //     return new Intl.NumberFormat('en-US', {
    //         style: 'currency',
    //         currency: 'USD',
    //     }).format(price);
    // };

    const formatDate = (dateString) => {
        if (!dateString) return 'Date not available';
        return new Date(dateString).toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="h-full bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Holiday Packages</h1>
                            <p className="text-gray-600">
                                Fetched {packages.length} packages{pagination ? ` • Page ${pagination.currentPage} of ${pagination.totalPages}` : ''}
                            </p>
                        </div>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="btn-primary flex items-center"
                            >
                                <PlusIcon className="h-5 w-5 mr-2" />
                                Add Package
                            </button>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="btn-secondary flex items-center"
                            >
                                <FunnelIcon className="h-5 w-5 mr-2" />
                                {showFilters ? 'Hide Filters' : 'Show Filters'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="bg-white border-b border-gray-200 p-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Search Box */}
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Search
                            </label>
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search packages..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                            </div>
                        </div>

                        {/* Destination Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Destination
                            </label>
                            <DestinationAutocomplete
                                value={filters.destination}
                                onChange={(value) => handleFilterChange('destination', value)}
                                error={null}
                                disabled={false}
                            />
                        </div>

                        {/* Category Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category
                            </label>
                            <div className="relative">
                                <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                <select
                                    value={filters.category}
                                    onChange={(e) => handleFilterChange('category', e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none"
                                >
                                    <option value="">All Categories</option>
                                    {categories.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Filter Actions */}
                        <div className="flex items-end space-x-2">
                            <button
                                onClick={handleApplyFilters}
                                className="btn-primary flex-1"
                            >
                                Apply Filters
                            </button>
                            <button
                                onClick={handleClearFilters}
                                className="btn-secondary flex items-center"
                                title="Clear all filters"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className="p-6">
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                {packages.length === 0 ? (
                    <div className="text-center py-16">
                        <MapPinIcon className="mx-auto h-16 w-16 text-gray-400" />
                        <h3 className="mt-4 text-lg font-medium text-gray-900">No packages found</h3>
                        <p className="mt-2 text-sm text-gray-500">
                            {Object.values(filters).some(f => f)
                                ? 'Try adjusting your filters to see more results.'
                                : 'No holiday packages available at the moment.'
                            }
                        </p>
                    </div>
                ) : (
                    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Package Details
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Destination
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Category
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date Slots
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {packages.map((pkg) => (
                                        <tr key={pkg._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {pkg.title || 'Untitled Package'}
                                                    </div>
                                                    <div className="text-sm text-gray-500 max-w-xs truncate">
                                                        {pkg.description || 'No description available'}
                                                    </div>
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        Vendor: {pkg.vendor?.name || 'Unknown'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm text-gray-900">
                                                    <MapPinIcon className="h-4 w-4 mr-2 text-gray-400" />
                                                    {pkg.destination || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm text-gray-900">
                                                    <TagIcon className="h-4 w-4 mr-2 text-gray-400" />
                                                    {pkg.category || 'N/A'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {pkg.type === 1 ? 'Non-Schedulable Package' : pkg.type === 2 ? 'Schedulable Package' : 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="relative min-w-[120px]">
                                                    <select
                                                        value={pkg.status || 'DRAFT'}
                                                        onChange={(e) => handleStatusChange(pkg._id, e.target.value)}
                                                        className={`appearance-none w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 pr-8 ${getStatusColor(pkg.status || 'DRAFT')}`}
                                                    >
                                                        <option value="DRAFT" className="text-yellow-600 bg-yellow-100">DRAFT</option>
                                                        <option value="PUBLISHED" className="text-green-600 bg-green-100">PUBLISHED</option>
                                                        {/* <option value="SUSPENDED" className="text-yellow-600 bg-yellow-100">SUSPENDED</option> */}
                                                        <option value="ARCHIVED" className="text-red-600 bg-red-100">ARCHIVED</option>
                                                    </select>
                                                    <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {pkg.type === 1 ? '-' : (pkg.dateSlots?.length || 0) + ' slots'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {formatDate(pkg.createdAt)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    {/* <button
                            className="text-gray-400 hover:text-primary-600"
                            title="View details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button> */}
                                                    <button
                                                        onClick={() => handleEditClick(pkg)}
                                                        className={`${pkg.status === 'PUBLISHED' ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-primary-600'}`}
                                                        title={pkg.status === 'PUBLISHED' ? 'Cannot edit published package' : 'Edit package'}
                                                        disabled={pkg.status === 'PUBLISHED'}
                                                    >
                                                        <PencilIcon className="h-5 w-5" />
                                                    </button>
                                                    {pkg.type !== 1 && (
                                                        <button
                                                            onClick={() => handleDateSlotsClick(pkg)}
                                                            className={`${pkg.status === 'PUBLISHED' ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-blue-600'}`}
                                                            title={pkg.status === 'PUBLISHED' ? 'Cannot manage slots for published package' : 'Manage date slots'}
                                                            disabled={pkg.status === 'PUBLISHED'}
                                                        >
                                                            <TicketIcon className="h-5 w-5" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDeleteClick(pkg._id, pkg.title)}
                                                        className="text-gray-400 hover:text-red-600"
                                                        title="Delete package"
                                                    >
                                                        <TrashIcon className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {pagination && (
                            <div className="mt-4 flex items-center justify-between">
                                <div className="text-xs text-gray-500">
                                    Page {pagination.currentPage} of {pagination.totalPages} • Total {pagination.totalPackages || packages.length}
                                </div>
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={!pagination.hasPrev}
                                        className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="text-xs text-gray-500">
                                        {pagination.currentPage} / {pagination.totalPages}
                                    </span>
                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={!pagination.hasNext}
                                        className="px-3 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* Add Package Form Sidebar */}
            <HolidayPackageForm
                isOpen={showAddForm}
                onClose={() => setShowAddForm(false)}
                onSuccess={handleAddSuccess}
            />

            {/* Edit Package Form Sidebar */}
            <HolidayPackageForm
                isOpen={!!editPackage}
                onClose={() => setEditPackage(null)}
                onSuccess={handleEditSuccess}
                editPackage={editPackage}
            />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Holiday Package"
        message="Are you sure you want to delete this holiday package? This action cannot be undone."
        itemName={deleteDialog.packageTitle}
        loading={deleteDialog.loading}
      />

      {/* Date Slots Sidebar */}
      <DateSlotsSidebar
        isOpen={!!selectedPackageForSlots}
        onClose={() => setSelectedPackageForSlots(null)}
        packageData={selectedPackageForSlots}
        onSlotAdded={handleSlotAdded}
      />
    </div>
  );
};

export default HolidayPackagesList;
